import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Optional,
  ElementRef,
  ViewChild,
  Inject,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { MatLegacyAutocomplete as MatAutocomplete } from '@angular/material/legacy-autocomplete';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// SERVICES

// CONFIGS
import { SnackBarConfig } from '../../../shared';

// INTERFACES
import {
  ELogicalOperators,
  EComparisonOperators,
} from '../../../shared/interfaces/operator.interface';
import { AppIntegrationAccountSettingsApiService } from '../../../shared/services/api/app-integration/app-integration-account-settings-api.service';

// UTILS
import {
  trackByValue,
  trackByControlValue,
  trackById,
  trackByLabel,
} from '../../../shared/util';
import { getDisplayTextForComparisonOperator } from '../../../shared/util/operator.utils';
import { completeRowConditionValidator } from '../../../shared/validators/condition-validator';
import { EAppIntegrationAccountSettingsKey } from '../../intefaces/app-integration-account-settings.interface';

@Component({
  selector: 'lum-app-integration-account-product-filter-form',
  templateUrl: './app-integration-account-product-filter-form.component.html',
  styleUrls: ['./app-integration-account-product-filter-form.component.scss'],
})
export class AppIntegrationAccountProductFilterFormComponent
  implements OnInit, OnDestroy
{
  @ViewChild('autocompleteInput')
  autocompleteInput :ElementRef<HTMLInputElement>;

  @ViewChild('auto')
  auto :MatAutocomplete;

  filterSetting :any = null;
  appIntegrationAccountId :number = 0;
  submittingForm :boolean = false;
  loading :boolean = false;
  form :UntypedFormGroup;
  logicalOperators :{ value :string | null; label :string }[];
  comparisonOperators :{ value :string | null; label :string }[];

  trackByLabel = trackByLabel;
  trackByValue = trackByValue;
  trackById = trackById;
  trackByControlValue = trackByControlValue;

  private onDestroy$ = new Subject();

  constructor(
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private snackBar :MatSnackBar,
    private appIntegrationAccountSettingsApiService :AppIntegrationAccountSettingsApiService,
    @Inject(MAT_DIALOG_DATA) public data :{ appIntegrationAccountId :number },
    @Optional()
    public dialogRef :MatDialogRef<AppIntegrationAccountProductFilterFormComponent>,
  ) {}

  ngOnInit() :void {
    this.appIntegrationAccountId = this.data.appIntegrationAccountId;
    this.loadData();
    this.initForm();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  drop(event :CdkDragDrop<string[]>) :void {
    const { previousIndex, currentIndex } = event;
    const control = this.conditionsFormArray.at(previousIndex);
    this.conditionsFormArray.removeAt(previousIndex);
    this.conditionsFormArray.insert(currentIndex, control);
  }

  onRemoveCondition(i :number) :void {
    this.conditionsFormArray.removeAt(i);
  }

  onAddCondition(condition ?:UntypedFormGroup) :void {
    if (!condition) {
      const conditionGroup = this.fb.group({
        field: [null],
        operator: [null],
        value: [null],
      });

      this.conditionsFormArray.push(conditionGroup);
    } else {
      this.conditionsFormArray.push(condition);
    }
  }

  onAddMultipleConditions(rows :number) :void {
    for (let condition = 0; condition < rows; condition++) {
      this.onAddCondition();
    }
  }

  onSubmit() :void {
    this.form.markAllAsTouched();

    if (
      this.form.get('condition')?.errors?.['atLeastOneCompleteConditionRequired']
    ) {
      this.snackBar.open('At least one complete condition is required.', 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    if (this.form.get('condition')?.errors?.['incompleteCondition']) {
      this.snackBar.open(
        'Please complete all condition rows or clear the incomplete ones.',
        'Fail',
        {
          duration: SnackBarConfig.DEFAULT.duration,
        },
      );

      return;
    }

    if (this.form.invalid) {
      return;
    }

    const conditions = this.conditionsFormArray.controls
      .map((control) => control.value)
      .filter(({ field, operator, value }) => field || operator || value)
      .map((condition, index) => ({ ...condition, order: index }));
    this.submittingForm = true;
    const data = {
      operator: this.form.value.operator,
      conditions: conditions,
    };

    try {
      if (this.filterSetting) {
        this.appIntegrationAccountSettingsApiService
          .update(this.filterSetting.id, {
            appIntegrationAccountId: this.data.appIntegrationAccountId,
            key: EAppIntegrationAccountSettingsKey.GET_PRODUCTS_FILTER,
            value: JSON.stringify(data),
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              this.snackBar.open('Filter updated', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });

              this.dialogRef.close(response);
            },
            error: (responseError) => {
              this.snackBar.open(
                responseError?.error
                  ? responseError.error
                  : 'Error updating record',
                'Fail',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );
            },
          });
      } else {
        this.appIntegrationAccountSettingsApiService
          .create({
            appIntegrationAccountId: this.data.appIntegrationAccountId,
            key: EAppIntegrationAccountSettingsKey.GET_PRODUCTS_FILTER,
            value: JSON.stringify(data),
          })
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              this.snackBar.open('Filter created', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });

              this.dialogRef.close(response);
            },
            error: (responseError) => {
              this.snackBar.open(
                responseError?.error
                  ? responseError.error
                  : 'Error creating record',
                'Fail',
                {
                  duration: SnackBarConfig.DEFAULT.duration,
                },
              );
            },
          });
      }
    } catch (error :any) {
      this.submittingForm = false;
      this.snackBar.open(error, 'Fail', {
        duration: SnackBarConfig.DEFAULT.duration,
      });
      this.cd.detectChanges();
    }
  }

  private initForm() :void {
    this.form = this.fb.group({
      operator: [null, [Validators.required]],
      condition: this.fb.array([], [completeRowConditionValidator()]),
    });

    if (!this.appIntegrationAccountId) {
      this.addBlankConditionRow();
    }

    this.logicalOperators = Object.entries(ELogicalOperators).map(
      ([label, value]) => {
        return { value, label };
      },
    );

    this.comparisonOperators = Object.entries(EComparisonOperators).map(
      ([label, value]) => {
        return {
          value: value,
          label: getDisplayTextForComparisonOperator(value),
        };
      },
    );
  }

  private addBlankConditionRow() :void {
    const conditionGroup = this.fb.group({
      field: [null],
      operator: [null],
      value: [null],
    });

    this.conditionsFormArray.push(conditionGroup);
  }

  private setFormValue() :void {
    if (this.filterSetting) {
      if (this.filterSetting?.value?.operator) {
        this.form.patchValue({
          operator: this.filterSetting.value.operator ?? null,
        });
      }

      if (
        this.filterSetting?.value?.conditions &&
        this.filterSetting.value.conditions.length > 0
      ) {
        this.filterSetting.value.conditions.forEach((condition) => {
          this.onAddCondition(this.fb.group(condition));
        });
      } else {
        this.addBlankConditionRow();
      }
    }
    this.cd.detectChanges();
  }

  private loadData() :void {
    this.loading = true;
    this.appIntegrationAccountSettingsApiService
      .getById(this.appIntegrationAccountId, {
        key: EAppIntegrationAccountSettingsKey.GET_PRODUCTS_FILTER,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.loading = false;
          if (response && response.value) {
            response.value = JSON.parse(response.value);
            this.filterSetting = response;
          }
          this.setFormValue();
        },
        error: (response) => {
          this.loading = false;
          this.setFormValue();
        },
      });
  }

  get conditionsFormArray() :UntypedFormArray {
    return this.form.get('condition') as UntypedFormArray;
  }
}
